
import React from 'react';
import { Link } from 'react-router-dom';
import { Instagram, Facebook, Mail, MapPin, Clock, Phone } from 'lucide-react';
import { useLanguage } from '../i18n';
import { useSiteSettings } from '../hooks/useSiteSettings';

const Footer = () => {
  const { t, language } = useLanguage();
  const { settings } = useSiteSettings();

  // Dynamic content
  const brandName = settings?.brand_name || "Lou's Way";
  const address = settings?.address || "Galerias De Torres Loja 11B, Av. Gen. Humberto Delgado 32, 2560-272, Torres Vedras, Portugal";
  const phone = settings?.phone || "+351 915 534 864";
  const email = settings?.email || "lourdyane.rod@gmail.com";
  const instagram = settings?.instagram || "https://instagram.com/louswaystudio";
  const facebook = settings?.facebook || "https://facebook.com/jeitinhodalou";
  const businessHours = settings?.[`business_hours_${language}`] || t('footer.hours_text');
  const footerText = settings?.[`footer_text_${language}`] || t('footer.rights');

  return (
    <footer className="bg-[#1A1815] text-white" data-testid="footer">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand */}
          <div className="space-y-4">
            <h3 className="font-heading text-2xl font-semibold text-[#BFA05F]">
              {brandName}
            </h3>
            <p className="text-[#A8A29A] text-sm leading-relaxed">
              {settings?.[`hero_subtitle_${language}`] || t('home.hero_subtitle')}
            </p>
            <div className="flex gap-4 pt-4">
              <a
                href={instagram}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-[#2C2924] flex items-center justify-center hover:bg-[#BFA05F] transition-colors"
                data-testid="social-instagram"
              >
                <Instagram className="w-5 h-5" />
              </a>
              <a
                href={facebook}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-[#2C2924] flex items-center justify-center hover:bg-[#BFA05F] transition-colors"
                data-testid="social-facebook"
              >
                <Facebook className="w-5 h-5" />
              </a>
              <a
                href={`mailto:${email}`}
                className="w-10 h-10 rounded-full bg-[#2C2924] flex items-center justify-center hover:bg-[#BFA05F] transition-colors"
                data-testid="social-email"
              >
                <Mail className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div className="space-y-4">
            <h4 className="text-sm uppercase tracking-widest text-[#BFA05F]">
              Links
            </h4>
            <nav className="flex flex-col gap-3">
              <Link
                to="/services"
                className="text-[#A8A29A] hover:text-white transition-colors"
              >
                {t('nav.services')}
              </Link>
              <Link
                to="/about"
                className="text-[#A8A29A] hover:text-white transition-colors"
              >
                {t('nav.about')}
              </Link>
              <Link
                to="/culture"
                className="text-[#A8A29A] hover:text-white transition-colors"
              >
                {t('nav.culture')}
              </Link>
              <Link
                to="/mission"
                className="text-[#A8A29A] hover:text-white transition-colors"
              >
                {t('nav.mission')}
              </Link>
            </nav>
          </div>

          {/* Contact */}
          <div className="space-y-4">
            <h4 className="text-sm uppercase tracking-widest text-[#BFA05F]">
              {t('footer.contact')}
            </h4>
            <div className="space-y-3">
              <div className="flex items-start gap-3 text-[#A8A29A]">
                <MapPin className="w-5 h-5 mt-0.5 text-[#BFA05F] flex-shrink-0" />
                <span className="text-sm">{address}</span>
              </div>
              <div className="flex items-center gap-3 text-[#A8A29A]">
                <Phone className="w-5 h-5 text-[#BFA05F] flex-shrink-0" />
                <a
                  href={`tel:${phone}`}
                  className="text-sm hover:text-white transition-colors"
                >
                  {phone}
                </a>
              </div>
              <div className="flex items-center gap-3 text-[#A8A29A]">
                <Clock className="w-5 h-5 text-[#BFA05F] flex-shrink-0" />
                <span className="text-sm">{businessHours}</span>
              </div>
              <div className="flex items-center gap-3 text-[#A8A29A]">
                <Mail className="w-5 h-5 text-[#BFA05F] flex-shrink-0" />
                <a
                  href={`mailto:${email}`}
                  className="text-sm hover:text-white transition-colors"
                >
                  {email}
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom */}
        <div className="mt-12 pt-8 border-t border-[#2C2924] flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-[#635E54] text-sm">
            © {new Date().getFullYear()} {brandName}. {footerText}
          </p>
          <div className="flex flex-wrap justify-center gap-6 text-sm text-[#635E54]">
            <Link
              to="/politica-de-privacidade"
              className="hover:text-white transition-colors"
            >
              {language === 'pt' ? 'Política de Privacidade' : 'Privacy Policy'}
            </Link>
            <Link
              to="/exclusao-de-dados"
              className="hover:text-white transition-colors"
            >
              {language === 'pt' ? 'Exclusão de Dados do Utilizador' : 'User Data Deletion'}
            </Link>
            <Link
  to="/termos-de-servico"
  className="hover:text-white transition-colors"
>
  {language === 'pt' ? 'Termos de Serviço' : 'Terms of Service'}
</Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
